import { useParams, Link } from 'react-router-dom';
import { CalendarToday, ChevronRight, Person } from '@mui/icons-material';
import { Badge, Button, Card, CardContent, CardHeader } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { CardTitle } from '../components/card-title';
import { DataTable } from '../components/data-table';
import { LoadingState } from '../components/loading-state';
import { useGetAppointmentDetailsQuery } from '../store/api';
import { formatDateTime } from '../utils/formatting';

export default function AppointmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const appointmentId = id || ''; // Keep as string since it's a UUID

  // Always call the appointment details query
  const { data, isLoading } = useGetAppointmentDetailsQuery(
    { appointmentId },
    {
      skip: !appointmentId,
    },
  );

  // Extract provider data from appointment response
  const providerData = data?.provider;
  const providerId = data?.appointment?.provider_id;

  // If we don't have the required appointmentId, show error
  if (!appointmentId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Appointment not found</h2>
            <p className="text-gray-600 mt-2">Invalid appointment ID.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState message="Loading appointment details..." />;
  }

  if (!data?.appointment) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Appointment not found</h2>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const { appointment, encounter, notes, transcriptions } = data;

  const getStatusBadgeColor = (
    status: string | undefined | null,
  ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'active':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'failed':
      case 'error':
      case 'cancelled':
        return 'error';
      case 'draft':
      case 'inactive':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const notesColumns = [
    {
      key: 'templateName',
      label: 'Template',
      render: (value: any) => value || 'Custom Note',
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value: any) => formatDateTime(value),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: any) => {
        const color = getStatusBadgeColor(value);
        return <Badge color={color}>{value}</Badge>;
      },
    },
  ];

  const transcriptionsColumns = [
    {
      key: 'id',
      label: 'Session ID',
      render: (_value: any, row: any) => {
        const id = row.transcription_session_id || row.id || row.transcriptionSessionId;
        return id ? id.slice(0, 8) + '...' : 'N/A';
      },
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value: any) => formatDateTime(value),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: any) => {
        const color = getStatusBadgeColor(value);
        return <Badge color={color}>{value || 'Unknown'}</Badge>;
      },
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton />
        </div>
        {/* Breadcrumb */}
        <nav className="flex items-center mb-6 text-sm">
          <Link to="/admin-v2">
            <Button
              variant="text"
              className="text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              Admin
            </Button>
          </Link>
          <ChevronRight className="mx-1 h-4 w-4 text-gray-400" />
          {providerId && (
            <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}>
              <Button
                variant="text"
                className="text-sm font-medium text-gray-700 hover:text-blue-600"
              >
                {providerData?.name || 'Provider'}
              </Button>
            </Link>
          )}
          <ChevronRight className="mx-1 h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-900">Appointment</span>
        </nav>

        {/* Appointment Header */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Appointment Details</h1>
              <p className="text-sm text-gray-500 mt-1">
                ID: {appointment.appointment_id || appointment.id}
              </p>
            </div>
            <Badge color={getStatusBadgeColor(appointment.status)} className="text-sm">
              {appointment.status}
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Scheduled Time</h3>
              <p className="mt-1 text-sm text-gray-900">
                {formatDateTime(appointment.scheduled_time || appointment.scheduledTime)}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Duration</h3>
              <p className="mt-1 text-sm text-gray-900">{appointment.duration || 30} minutes</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Type</h3>
              <p className="mt-1 text-sm text-gray-900">{appointment.type || 'General'}</p>
            </div>
          </div>

          {appointment.notes && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-500">Notes</h3>
              <p className="mt-1 text-sm text-gray-900">{appointment.notes}</p>
            </div>
          )}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Provider Info */}
          <div className="space-y-6">
            {/* Provider Card */}
            <Card>
              <CardHeader>
                <CardTitle>
                  <Person className="mr-2" />
                  Provider
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-900">
                    {providerData?.name || 'Unknown Provider'}
                  </div>
                  <div className="text-sm text-gray-500">
                    {providerData?.email || 'No email available'}
                  </div>
                  {providerId && <div className="text-xs text-gray-400">ID: {providerId}</div>}
                  {providerId && (
                    <div className="mt-3">
                      <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}>
                        <Button variant="outlined" size="small" className="w-full">
                          View Provider
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Encounter Card */}
            {encounter && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    <CalendarToday className="mr-2" />
                    Related Encounter
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-900">
                      Encounter #{encounter.encounter_id?.slice(0, 8)}...
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDateTime(encounter.created_at)}
                    </div>
                    <Badge color={getStatusBadgeColor(encounter.status)} className="text-xs">
                      {encounter.status}
                    </Badge>
                    <div className="mt-3">
                      <Link to={`/admin-v2/encounter/${encounter.encounter_id}`}>
                        <Button variant="outlined" size="small" className="w-full">
                          View Encounter
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Notes and Transcriptions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Notes Table */}
            <DataTable
              title="Related Notes"
              columns={notesColumns as any}
              data={notes || []}
              loading={!notes}
              getRowUrl={note => {
                const noteId = note.note_id || note.noteId || note.id;
                return noteId ? `/admin-v2/note/${noteId}` : '';
              }}
              emptyMessage="No notes found for this appointment"
            />

            {/* Transcriptions Table */}
            <DataTable
              title="Related Transcriptions"
              columns={transcriptionsColumns as any}
              data={transcriptions || []}
              loading={!transcriptions}
              getRowUrl={() => ''}
              emptyMessage="Coming soon! 😘"
            />
          </div>
        </div>
      </main>
    </div>
  );
}
